import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTranslation } from 'react-i18next';

import { applyVisibilityPreset } from 'src/helpers/pageContentUtils';
import { VisibilityPresetId } from 'src/helpers/pageContentBlockPresets';
import {
  IVisibilityFilter,
  IVisibilityRule,
  VisibilityConnector,
  VisibilityPredicate,
} from 'src/interfaces/PageBlock';

const PREDICATES: VisibilityPredicate[] = [
  'activeEdition',
  'beforeSignupDeadline',
  'afterSignupDeadline',
  'isSignupOpen',
  'canShowResults',
  'userLoggedIn',
];

const SIGNUP_PRESET_IDS: VisibilityPresetId[] = [
  'signupOpen',
  'signupOpenWithEdition',
  'signupOpenGuest',
  'signupOpenLoggedIn',
  'signupClosed',
];

interface Props {
  value?: IVisibilityFilter;
  onChange: (value: IVisibilityFilter | undefined) => void;
  showSignupPresets?: boolean;
}

export default function VisibilityRuleBuilder({
  value,
  onChange,
  showSignupPresets = false,
}: Props) {
  const { t } = useTranslation();
  const rules = value?.rules ?? [];
  const connectors = value?.connectors ?? [];

  const updateRules = (nextRules: IVisibilityRule[], nextConnectors: VisibilityConnector[]) => {
    if (nextRules.length === 0) {
      onChange(undefined);
      return;
    }
    onChange({ rules: nextRules, connectors: nextConnectors });
  };

  const addRule = () => {
    const nextRules: IVisibilityRule[] = [
      ...rules,
      { predicate: 'activeEdition', expected: true },
    ];
    const nextConnectors =
      nextRules.length > 1 ? [...connectors, 'and' as VisibilityConnector] : [];
    updateRules(nextRules, nextConnectors);
  };

  const removeRule = (index: number) => {
    const nextRules = rules.filter((_, i) => i !== index);
    const nextConnectors = connectors.filter((_, i) => {
      if (index === 0) return i !== 0;
      if (index <= connectors.length) return i !== index - 1;
      return true;
    });
    updateRules(nextRules, nextConnectors);
  };

  const updateRule = (index: number, patch: Partial<IVisibilityRule>) => {
    const nextRules = rules.map((r, i) => (i === index ? { ...r, ...patch } : r));
    updateRules(nextRules, connectors);
  };

  const updateConnector = (index: number, connector: VisibilityConnector) => {
    const next = [...connectors];
    next[index] = connector;
    updateRules(rules, next);
  };

  return (
    <Box>
      {showSignupPresets ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {SIGNUP_PRESET_IDS.map(presetId => (
            <Button
              key={presetId}
              size="small"
              variant="outlined"
              onClick={() => onChange(applyVisibilityPreset(presetId))}
            >
              {t(`system.pages.signupVisibilityPresets.${presetId}`)}
            </Button>
          ))}
        </Box>
      ) : null}
      {rules.map((rule, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
            mt: index > 0 ? 1.5 : 0,
          }}
        >
          {index > 0 ? (
            <FormControl size="small" sx={{ minWidth: 88 }}>
              <Select
                value={connectors[index - 1] ?? 'and'}
                onChange={e =>
                  updateConnector(index - 1, e.target.value as VisibilityConnector)
                }
                aria-label={t('system.pages.visibilityRules')}
              >
                <MenuItem value="and">{t('system.pages.connectorAnd')}</MenuItem>
                <MenuItem value="or">{t('system.pages.connectorOr')}</MenuItem>
              </Select>
            </FormControl>
          ) : null}
          <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
            <InputLabel>{t('system.pages.condition')}</InputLabel>
            <Select
              label={t('system.pages.condition')}
              value={rule.predicate}
              onChange={e =>
                updateRule(index, { predicate: e.target.value as VisibilityPredicate })
              }
            >
              {PREDICATES.map(p => (
                <MenuItem key={p} value={p}>
                  {t(`system.pages.predicates.${p}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>{t('system.pages.expected')}</InputLabel>
            <Select
              label={t('system.pages.expected')}
              value={rule.expected ? 'true' : 'false'}
              onChange={e => updateRule(index, { expected: e.target.value === 'true' })}
            >
              <MenuItem value="true">{t('system.pages.isTrue')}</MenuItem>
              <MenuItem value="false">{t('system.pages.isFalse')}</MenuItem>
            </Select>
          </FormControl>
          <IconButton
            size="small"
            onClick={() => removeRule(index)}
            aria-label={t('base.delete')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addRule} sx={{ mt: 1 }}>
        {t('system.pages.addCondition')}
      </Button>
    </Box>
  );
}
